import type { ReactElement } from 'react';
import { Sheet } from './Sheet';
import { Icon } from './Icon';
import type { SheetName } from './Toolbar';

/** Was am gewählten Clip noch zu tun ist -- das Seltene, das nicht in der Reihe steht. */
type Tun = {
  springeKeyframe: (richtung: 1 | -1) => void;
  alleKeyframesWeg: () => void;
  nachruecken: () => void;
  mehrfachUmschalten: () => void;
  gruppieren: () => void;
  gruppeLoesen: () => void;
};

/**
 * Das Blatt hinter „Mehr“ (21. September, Saschas Bitte um fünf Knöpfe).
 *
 * Hier stand vorher eine Reihe aus Eltern- und Kindknöpfen in der Leiste. Was
 * daran nicht ging: die Kinder schoben die Leiste zur Seite, und ein Symbol
 * ohne Satz sagt nicht, was es tut. Jetzt eine Zeile je Sache, mit dem Stand
 * daneben -- wie viele Keyframes, ob stumm, ob nachgerückt wird.
 *
 * Seit dem 12. Oktober stehen Keyframe, Stumm, Duplizieren und Kopieren
 * wieder als Knöpfe in der (jetzt rollenden) Reihe; hier bleibt das Seltene.
 *
 * Was das Blatt *nicht* zumacht: die Keyframe-Zeilen und die Schalter. Wer
 * einen Keyframe setzt, setzt oft mehrere, und wer stumm schaltet, will es
 * sehen. Was in ein anderes Blatt führt (Pegel, Text), macht es zu.
 */
export function ClipMehrSheet({
  onClose,
  onOpen,
  eines,
  spurArt,
  anzahl,
  gruppiert,
  keyframes,
  pegelpunkte,
  nachrueckenAn,
  mehrfach,
  istText,
  hatTon,
  tun,
}: {
  onClose: () => void;
  onOpen: (sheet: SheetName) => void;
  eines: unknown;
  spurArt: 'video' | 'audio' | null;
  anzahl: number;
  gruppiert: boolean;
  keyframes: number;
  pegelpunkte: number;
  nachrueckenAn: boolean;
  mehrfach: boolean;
  istText: boolean;
  hatTon: boolean;
  tun: Tun;
}): ReactElement {
  const eins = eines !== null;
  const zeile = (
    id: string,
    icon: string,
    titel: string,
    satz: string,
    handeln: () => void,
    extra?: { bleibt?: boolean; wert?: string; gefahr?: boolean },
  ): ReactElement => (
    <button
      key={id}
      className={`row ${extra?.gefahr ? 'danger' : ''}`.trim()}
      data-mehr={id}
      style={{ textAlign: 'left', width: '100%' }}
      onClick={() => {
        handeln();
        if (!extra?.bleibt) onClose();
      }}
    >
      <Icon name={icon} />
      <div className="grow">
        <div className="title">{titel}</div>
        <div className="sub">{satz}</div>
      </div>
      {extra?.wert ? <span className="badge">{extra.wert}</span> : null}
    </button>
  );

  return (
    <Sheet title={anzahl > 1 ? `Mehr (${anzahl} Clips)` : 'Mehr'} onClose={onClose}>
      <div className="list">
        {eins && spurArt === 'video'
          ? [
              keyframes > 0
                ? zeile('keyframe-vor', 'stepBack', 'Voriger Keyframe', 'Springt zur Raute davor', () => tun.springeKeyframe(-1), { bleibt: true })
                : null,
              keyframes > 0
                ? zeile('keyframe-nach', 'stepForward', 'Nächster Keyframe', 'Springt zur Raute danach', () => tun.springeKeyframe(1), { bleibt: true })
                : null,
              keyframes > 1
                ? zeile('keyframe-weg', 'trash', 'Alle Keyframes weg', 'Die Ebene steht danach still', tun.alleKeyframesWeg, { bleibt: true })
                : null,
            ]
          : null}
        {eins && hatTon
          ? [
              // Führt in ein anderes Blatt: dann darf dieses nicht auch noch
              // „zu“ sagen -- das setzte die Wahl gleich wieder auf nichts.
              zeile('pegel', 'music', 'Pegel', 'Lautstärke über die Zeit, mit Punkten im Verlauf', () => onOpen('audio'), {
                bleibt: true,
                wert: pegelpunkte ? String(pegelpunkte) : undefined,
              }),
            ]
          : null}
        {/* Bei mehreren: der Ton aller zugleich (7.209). */}
        {anzahl > 1
          ? zeile('alle-ton', 'music', `Ton von ${anzahl} Clips`, 'Alle um dieselben dB lauter oder leiser, normalisieren, stumm', () => onOpen('audio'), { bleibt: true })
          : null}
        {istText ? zeile('text', 'text', 'Text bearbeiten', 'Wortlaut, Schrift, Farbe und Kasten', () => onOpen('brand'), { bleibt: true }) : null}
        {zeile(
          'nachruecken',
          'nachruecken',
          'Nachrücken beim Kürzen',
          'Kürzen zieht alles dahinter nach, statt eine Lücke zu lassen',
          tun.nachruecken,
          { bleibt: true, wert: nachrueckenAn ? 'an' : 'aus' },
        )}
        {zeile(
          'mehrfach',
          mehrfach ? 'check' : 'plus',
          mehrfach ? 'Mehrfachauswahl beenden' : 'Mehr wählen',
          'Weitere Clips antippen und zusammen bearbeiten',
          tun.mehrfachUmschalten,
          { bleibt: true, wert: mehrfach ? 'an' : undefined },
        )}
        {gruppiert
          ? zeile('gruppe-loesen', 'ungroup', 'Gruppe lösen', 'Die Clips bewegen sich wieder einzeln', tun.gruppeLoesen)
          : anzahl > 1
            ? zeile('gruppieren', 'group', `${anzahl} gruppieren`, 'Bild und Ton bewegen sich zusammen', tun.gruppieren)
            : null}
      </div>
    </Sheet>
  );
}
